/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { useMemo } from "react";

import { useAuth, useWatchForm } from "hooks";
import sortBy from "lodash/sortBy";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { Status } from "services/types";
import { getLabelPayrollCycle, getLanContent } from "utils";

import {
  templateDetailFieldMeService,
  templateListByUserService,
} from "./services";
import { Template } from "./types";

interface PayrollCycleParent {
  id: string;
  payrollCycle: PayrollCycle;
}

interface PayrollCycle {
  id: string;
  status: number;
  type: string;
  period: number;
  from: number;
  to: number;
  cutOffDate: number;
  fromPayDate: number;
  toPayDate: number;
  payDate: null;
  isPayDateNextCycle: boolean;
}

export const useListPayrollTemplateByUser = ({
  methods,
  keyTemplate,
  keyTemplatePeriod,
  enabled = true,
  keyQuery,
  defaultTemplateId,
  enabledField,
  onSuccess,
  onSetField,
}: {
  methods?: ReturnType<typeof useForm>;
  keyTemplate?: string;
  keyTemplatePeriod?: string;
  enabled?: boolean;
  keyQuery?: string;
  defaultTemplateId?: string;
  enabledField?: boolean;
  onSuccess?: (e: {
    label: string;
    value: string;
    original: Template;
    cycleId?: string;
    templatePeriod: number;
  }) => void;
  onSetField?: (name: string, value: any) => void;
}) => {
  const cycle = methods?.watch("cycleId");
  const { data } = useAuth();
  const templateId: any =
    methods?.watch(keyTemplate as never) ||
    ({} as { value: string; original: Template });
  const templatePeriodId: any =
    methods?.watch(keyTemplatePeriod as never) || "";

  const templateList = useQuery({
    queryKey: ["templateListService", keyQuery],
    enabled,
    queryFn: () =>
      templateListByUserService({
        status: Status.Active,
        page: 1 as never,
        limit: 20,
      }),
    onSuccess: (e) => {
      if (onSuccess && e?.data?.items) {
        const defaultTemplateIdObject =
          e?.data?.items?.find((i) => i.id === defaultTemplateId) ||
          e?.data?.items?.[0];
        let cycles = defaultTemplateIdObject?.templatePayrollCycles?.map(
          (i) => i?.payrollCycle
        );
        cycles = sortBy(cycles || [], "period");
        const templatePeriods = sortBy(
          defaultTemplateIdObject?.templatePeriods || [],
          "period"
        );

        onSuccess({
          label: getLanContent(defaultTemplateIdObject, "name"),
          value: defaultTemplateIdObject?.id,
          original: defaultTemplateIdObject,
          cycleId: cycles?.[0]?.id || "",
          templatePeriod: +(templatePeriods?.[0]?.period || 0),
        });
      }
    },
  });
  useWatchForm({
    methods,
    callback: ({ name, type, value }) => {
      if (type === "change" && name === keyTemplate) {
        const currentTemplate = value?.[keyTemplate as never];
        const cycles = sortBy(
          currentTemplate?.original?.templatePayrollCycles?.map(
            (i: any) => i?.payrollCycle
          ) || [],
          "period"
        );
        const templatePeriods = sortBy(
          currentTemplate?.original?.templatePeriods || [],
          "period"
        );

        if (typeof onSetField === "function") {
          onSetField("cycleId", cycles?.[0]?.id || undefined);
          onSetField(
            keyTemplatePeriod || "",
            templatePeriods?.[0]?.period || undefined
          );
        }
        methods?.setValue(
          keyTemplatePeriod || "",
          templatePeriods?.[0]?.period || undefined
        );
        methods?.setValue("cycleId", cycles?.[0]?.id || undefined);
      }
    },
  });
  const templatePeriods = useMemo(() => {
    if (templateId?.original?.templatePeriods) {
      return (
        sortBy(templateId?.original?.templatePeriods || [], "period").map(
          (e: { period: string }) => ({
            label: e.period,
            value: e.period,
          })
        ) || []
      );
    }
    return [];
  }, [templateId?.original?.templatePeriods]);

  const templatePeriod = useMemo(() => {
    return templateId?.original?.templatePeriods?.find(
      (e: { period: string }) => +(e.period || 0) === +(templatePeriodId || "")
    );
  }, [templateId?.original?.templatePeriods, templatePeriodId]);

  const cycles = useMemo(() => {
    return (
      templateId?.original?.templatePayrollCycles?.map(
        (i: PayrollCycleParent) => i?.payrollCycle
      ) || []
    );
  }, [templateId?.original?.templatePayrollCycles]);

  const cycleOptions = useMemo(() => {
    return cycles.map((i: PayrollCycle) => ({
      label: getLabelPayrollCycle(i as never),
      value: i?.id,
    }));
  }, [cycles]);
  const cycleObject = useMemo(() => {
    const currentObject = cycles?.find((i: PayrollCycle) => i?.id === cycle);
    return currentObject || {};
  }, [cycle, cycles]);

  const templateFieldQuery = useQuery({
    queryKey: ["templateDetailFieldMeService", templateId?.original?.id],
    enabled: !!templateId?.original?.id && enabledField,
    queryFn: () =>
      templateDetailFieldMeService(templateId?.original?.id as never),
  });

  return {
    data: templateList?.data?.data?.items,
    empCode: data?.employeeCode,
    isFetched: templateList.isFetched,
    templatePeriods,
    templatePeriod,
    templateDetail: templateFieldQuery?.data?.data,

    periodData: {
      templatePeriod: cycleObject?.period || undefined,
      templateId: templateId?.value || undefined,
      cycleId: cycle || undefined,
    },
    templateId,
    periodState: {
      cycleOptions,
      cycles,
      cycleObject,
    },
  };
};
